import React, { useState } from 'react';
import { FadeIn, CTAButton, Section, SectionTitle, Accent, pad2 } from './Shared';
import { SecurePaymentNote } from './SecurePaymentNote';
import { useCountdown } from '../hooks/useCountdown';
import { markCheckoutStarted } from '../hooks/useExitIntent';
import { PRODUCTS } from './MetaPixel';
import { volumes, sessionFeatures, featureChips } from '../data/volumes';
import { bonuses } from '../data/bonuses';
import { Check, AlertTriangle, Plus, PrinterIcon, ArrowRightIcon, Lock, ZapIcon, ShieldCheck, BookOpenIcon, GiftIcon } from 'lucide-react';
export const ProductTomos = () => {
  const [openVolume, setOpenVolume] = useState<number | null>(0);
  return <Section className="bg-slate-950" data-id="element-28" data-mp="2cpykdjm5u63c">
      <FadeIn data-id="element-29" data-mp="2cox0cpn6299h">
        <SectionTitle sub="Elke twee maanden een nieuw deel. Open het, kies de sessie en geef les." data-id="element-30" data-mp="2cof43t0i7v1k">
          6 complete <Accent data-id="element-31" data-mp="30e9hcboqjlmc">delen</Accent>
        </SectionTitle>
      </FadeIn>

      {/* Year timeline */}
      <FadeIn delay={0.05} className="mx-auto mb-8 max-w-4xl" data-id="element-32" data-mp="357kgdqnb2y3p">
        <div className="grid grid-cols-6 gap-1.5 md:gap-2" aria-label="Je jaar, deel voor deel" data-id="element-33" data-mp="2hmmqk6x5j6vb">
          {volumes.map(v => <div key={v.number} data-id="element-34" data-mp="2cnt8s2au0tpc">
              <div className={`h-1.5 rounded-full ${v.bar}`} data-id="element-35" data-mp="2cknkuqdxvxv7" />
              <p className={`mt-2 font-display text-[11px] font-extrabold uppercase md:text-sm ${v.text}`} data-id="element-36" data-mp="2rc69fckv70q6">
                Deel {v.number}
              </p>
              <p className="hidden text-xs text-slate-500 md:block" data-id="element-37" data-mp="30bt2nb3fx085">{v.months}</p>
            </div>)}
        </div>
      </FadeIn>

      <FadeIn delay={0.1} className="mb-12 md:mb-16" data-id="element-38" data-mp="2w9fwwmop12bd">
        <ul className="mx-auto flex max-w-3xl flex-wrap justify-center gap-1.5 md:gap-2" data-id="element-39" data-mp="2wcu202oqrpmi">
          {featureChips.map(chip => <li key={chip} className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-slate-200 md:text-sm" data-id="element-40" data-mp="35bffvkj9qggy">
              <Check size={12} className="text-sky-400" strokeWidth={3} aria-hidden="true" data-id="element-41" data-mp="1axegmyjt3rgc" />
              {chip}
            </li>)}
        </ul>
        <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs text-slate-400 md:text-sm" data-id="element-42" data-mp="3b0yuem06t1qe">
          <PrinterIcon size={14} className="shrink-0" aria-hidden="true" data-id="element-43" data-mp="3fyqui3azju5o" />
          <span data-id="element-44" data-mp="3jzavjhyojhx3"><span className="font-semibold text-slate-200" data-id="element-45" data-mp="3jyix7zdp7634">Klaar om te printen</span> of gebruik het direct op je telefoon of tablet.</span>
        </p>
      </FadeIn>

      <h3 className="mb-5 text-center text-xs font-semibold uppercase tracking-widest text-slate-400" data-id="element-46" data-mp="3tys01w273prh">
        Wat je krijgt
      </h3>
      <div className="mb-16 grid grid-cols-1 items-start gap-3 md:mb-24 md:grid-cols-2 md:gap-4 lg:grid-cols-3" data-id="element-47" data-mp="2qe2qy5fvzmz">
        {volumes.map((v, i) => {
        const isOpen = openVolume === i;
        return <FadeIn key={v.number} delay={Math.min(i * 0.04, 0.2)} data-id="element-48" data-mp="bizazkbg2nxk" data-mpi={i}>
              <button type="button" onClick={() => setOpenVolume(isOpen ? null : i)} className={`group w-full rounded-2xl border border-t-[3px] bg-white/[0.03] p-5 text-left transition-[border-color,background-color] duration-200 ease-out hover:bg-white/[0.05] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 md:p-6 ${v.border} ${isOpen ? 'border-x-white/20 border-b-white/20' : 'border-x-white/10 border-b-white/10'}`} aria-expanded={isOpen} data-id="element-49" data-mp="gjvjycanl09u">
                <div className="flex items-start justify-between gap-3" data-id="element-50" data-mp="hnxbryjsao6g">
                  <div className="min-w-0" data-id="element-51" data-mp="1ed4och2fdmy8">
                    <p className={`font-display text-xs font-extrabold uppercase tracking-wide ${v.text}`} data-id="element-52" data-mp="1ec3gelgphgkq">
                      Deel {v.number} · {v.months}
                    </p>
                    <h4 className="mt-1.5 font-display text-lg font-extrabold leading-tight text-white md:text-xl" data-id="element-53" data-mp="14lih0vt7k80r">
                      {v.title}
                    </h4>
                    <p className="mt-1 text-sm text-slate-400" data-id="element-54" data-mp="1x3mcs9g85wds">{v.tagline}</p>
                  </div>
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/5 text-white transition-transform duration-200 ease-out ${isOpen ? 'rotate-45' : ''}`} aria-hidden="true" data-id="element-55" data-mp="1sce8frtr517h">
                    <Plus size={16} data-id="element-56" data-mp="1huiobam4uls3" />
                  </span>
                </div>
                <div className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-57" data-mp="13upz5jq98dm8">
                  <div className="overflow-hidden" data-id="element-58" data-mp="1vle4l7yyo55p">
                    <p className="pt-4 text-[15px] leading-relaxed text-slate-300" data-id="element-59" data-mp="1vlw7gng4phml">
                      {v.desc}
                    </p>
                    <p className="mt-3 inline-flex rounded-md bg-white/5 px-2 py-0.5 text-xs font-semibold text-slate-200" data-id="element-60" data-mp="1mt2czis42bo5">
                      {v.sessions}
                    </p>
                  </div>
                </div>
              </button>
            </FadeIn>;
      })}
      </div>

      <FadeIn data-id="element-61" data-mp="2ei5kd7fao250">
        <div className="grid gap-8 rounded-3xl border border-white/10 bg-slate-900/60 p-6 md:grid-cols-[0.9fr_1.1fr] md:gap-12 md:p-12" data-id="element-62" data-mp="2jagji827njwi">
          <div className="flex flex-col" data-id="element-63" data-mp="7uevz1g19gpt">
            <h3 className="text-center font-display text-3xl font-black uppercase italic leading-[0.95] tracking-tight text-white md:text-left md:text-5xl" data-id="element-64" data-mp="7tdcc8kyblwh">
              Elke sessie <Accent data-id="element-65" data-mp="hkreyp8791tu">bevat</Accent>
            </h3>
          </div>
          <ul className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2" data-id="element-66" data-mp="cm6govcics81">
            {sessionFeatures.map(feature => <li key={feature.title} className="flex items-start gap-3" data-id="element-67" data-mp="3l3awoxt94pae">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15" data-id="element-68" data-mp="3g3ok8ql8zd2n">
                  <Check size={12} className="text-emerald-400" strokeWidth={3} aria-hidden="true" data-id="element-69" data-mp="3v1h1poacubjh" />
                </span>
                <div data-id="element-70" data-mp="3q01bzix8fgur">
                  <p className="font-semibold leading-snug text-white" data-id="element-71" data-mp="3q3p72mibsbbl">{feature.title}</p>
                  <p className="text-sm leading-normal text-slate-400" data-id="element-72" data-mp="1acb2umfqr426">{feature.desc}</p>
                </div>
              </li>)}
          </ul>
        <p className="mt-5 text-center text-lg font-semibold leading-snug text-sky-300 md:mt-auto md:text-left md:text-xl" data-id="element-73" data-mp="1cetoe8vzq98e">
              Open het deel, kies de sessie en geef les. Zo simpel is het. 365
              lessen van topniveau. Vandaag klaar.
            </p></div>
      </FadeIn>
    </Section>;
};
export const Bonuses = () => {
  return <Section className="bg-slate-900/40" data-id="element-74" data-mp="1rat2c05s73lg">
      <FadeIn data-id="element-75" data-mp="to2gzcvkcswn">
        <SectionTitle sub="Exclusieve materialen — zonder extra kosten" data-id="element-76" data-mp="toln7wbupsuu">
          + 5 strategische <Accent data-id="element-77" data-mp="ymdr7oprt5fe">bonussen</Accent>
        </SectionTitle>
      </FadeIn>

      <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2 md:mb-10 md:gap-4 lg:grid-cols-5" data-id="element-78" data-mp="ykkkk9f5ql7e">
        {bonuses.map((bono, i) => <FadeIn key={bono.num} delay={Math.min(i * 0.04, 0.2)} className={i === bonuses.length - 1 ? 'sm:col-span-2 lg:col-span-1' : ''} data-id="element-79" data-mp="18d8feymkuf3h" data-mpi={i}>
            <article className="flex h-full gap-4 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-3 sm:flex-col sm:gap-0 sm:p-0" data-id="element-80" data-mp="awt2sqaulfyq">
              <img src={bono.image} alt={`Cover van bonus ${bono.num}: ${bono.title}`} loading="lazy" decoding="async" width={1024} height={1024} className="aspect-square w-28 shrink-0 self-start rounded-xl object-cover sm:w-full sm:rounded-none" data-id="element-81" data-mp="2t776hzq91dus" />
              <div className="flex min-w-0 flex-1 flex-col sm:p-4" data-id="element-82" data-mp="2k460s05aporn">
                <p className="font-display text-xs font-extrabold uppercase tracking-wide text-sky-400" data-id="element-83" data-mp="2f5uqxpf03e9f">
                  Bonus {bono.num}
                </p>
                <h3 className="mt-1 font-display text-base font-extrabold leading-tight text-white" data-id="element-84" data-mp="25h4grq9t4jzc">
                  {bono.title}
                </h3>
                <p className="mt-1.5 text-sm leading-snug text-slate-400" data-id="element-85" data-mp="21gauf1c0jd8k">
                  {bono.desc}
                </p>
                <div className="mt-auto flex items-center justify-between gap-2 pt-3" data-id="element-86" data-mp="1webphn0mk89i">
                  <span className="text-sm text-slate-500 line-through" data-id="element-87" data-mp="1rgjycv7t0q6d">
                    {bono.value}
                  </span>
                  <span className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-emerald-400" data-id="element-88" data-mp="3bcg0cczpan3b">
                    Gratis
                  </span>
                </div>
              </div>
            </article>
          </FadeIn>)}
      </div>

      <FadeIn className="mx-auto flex max-w-3xl flex-col items-center justify-between gap-4 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] px-6 py-5 text-center md:flex-row md:text-left" data-id="element-89" data-mp="31gcqsm2irm6c">
        <div className="flex items-center gap-3" data-id="element-90" data-mp="2rmm0rcqqv3oj">
          <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 md:flex" data-id="element-91" data-mp="2wg71hcjq2brf">
            <GiftIcon size={22} className="text-emerald-400" aria-hidden="true" data-id="element-92" data-mp="2ipbnxmya36hi" />
          </span>
          <div data-id="element-93" data-mp="2nnd6m6rsl3sg">
            <p className="text-sm text-slate-300" data-id="element-94" data-mp="2nkr3qkfujcgk">Totale waarde van de 5 bonussen: <span className="text-slate-500 line-through" data-id="element-95" data-mp="28qvxznazm39l">€50</span></p>
            <p className="text-xs text-slate-500" data-id="element-96" data-mp="2dp7bc80hqvwk">Inbegrepen bij Functionele Training 365, zonder extra kosten.</p>
          </div>
        </div>
        <p className="font-display text-3xl font-black uppercase italic leading-none tracking-tight text-emerald-400 md:text-4xl" data-id="element-97" data-mp="1u35qjpv0njpp">
          Vandaag gratis
        </p>
      </FadeIn>
    </Section>;
};
export const UrgencyCounter = () => {
  const {
    hours,
    minutes,
    seconds
  } = useCountdown();
  const units = [{
    value: hours,
    label: 'uur'
  }, {
    value: minutes,
    label: 'min'
  }, {
    value: seconds,
    label: 'sec'
  }];
  return <Section className="bg-slate-950 !py-14 md:!py-20" data-id="element-98" data-mp="290y44ch05y19">
      <FadeIn className="mx-auto max-w-xl text-center" data-id="element-99" data-mp="28ybwkcu43ij1">
        <p className="text-xs font-semibold uppercase tracking-widest text-sky-400" data-id="element-100" data-mp="2mqi3d8bdkd5t">
          Introductieprijs — eindigt over
        </p>
        <div className="mt-4 flex items-start justify-center gap-2 md:gap-3" role="timer" aria-live="off" data-id="element-101" data-mp="2hujezkq1qv5e">
          {units.map((unit, i) => <React.Fragment key={unit.label} data-id="element-102" data-mp="2rr626g5uhyal" data-mpi={i}>
              {i > 0 && <span className="pt-3 font-display text-4xl font-black text-slate-600 md:pt-4 md:text-5xl" aria-hidden="true" data-id="element-103" data-mp="2rsh1o3s9cjvw">:</span>}
              <div className="flex w-20 flex-col items-center rounded-2xl border border-white/10 bg-white/[0.04] py-3 md:w-24 md:py-4" data-id="element-104" data-mp="2m72dx0finovu">
                <span className="font-display text-4xl font-black tabular-nums leading-none tracking-tight text-white md:text-5xl" data-id="element-105" data-mp="2h8iqevl9y51d">
                  {pad2(unit.value)}
                </span>
                <span className="mt-1.5 text-[11px] font-semibold uppercase tracking-widest text-slate-500" data-id="element-106" data-mp="39vbvtr9lzshp">
                  {unit.label}
                </span>
              </div>
            </React.Fragment>)}
        </div>

        <p className="mt-6 flex items-center justify-center gap-2 text-base font-semibold text-amber-300 md:text-lg" data-id="element-107" data-mp="30u5czbjhm5la">
          <AlertTriangle size={18} className="shrink-0" aria-hidden="true" data-id="element-108" data-mp="2vyq28sgs0pt3" />
          Daarna gaat de prijs omhoog. Definitief.
        </p>

        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left" data-id="element-109" data-mp="1kh71d6qo4yxp">
          <div className="mb-3 flex justify-between gap-3 text-sm text-slate-300" data-id="element-110" data-mp="3w5bsqgcoc442">
            <span className="flex items-center gap-2" data-id="element-111" data-mp="4y949wo80bxj">
              <span className="h-2 w-2 shrink-0 rounded-full bg-sky-400 animate-pulse" aria-hidden="true" data-id="element-112" data-mp="4yqxekfl6a18" />
              Plekken tegen introductieprijs
            </span>
            <span className="shrink-0 font-semibold text-sky-400" data-id="element-113" data-mp="druinewh3r6f">Bijna op</span>
          </div>
          <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-white/10" role="progressbar" aria-valuenow={71} aria-valuemin={0} aria-valuemax={100} aria-label="Plekken al bezet" data-id="element-114" data-mp="iqp2mk66fls4">
            <div className="h-full rounded-full bg-blue-500" style={{
            width: '71%'
          }} data-id="element-115" data-mp="snto0hs77d7v" />
          </div>
          <p className="text-right text-xs text-slate-500" data-id="element-116" data-mp="wodi1chh60ps">
            71% van de plekken is al weg
          </p>
        </div>
      </FadeIn>
    </Section>;
};
export const OfferCard = ({
  id,
  footnote
}: {
  id?: string;
  footnote: string;
}) => {
  const includes = [{
    icon: BookOpenIcon,
    label: '6 complete delen',
    detail: '365 sessies, klaar om te geven',
    price: '€69'
  }, {
    icon: GiftIcon,
    label: '5 exclusieve bonussen',
    detail: 'Aanpassingen, warm-ups, afsluitingen & scripts',
    price: '€50'
  }];
  return <div id={id} className="relative rounded-3xl border border-sky-400/40 bg-slate-900 p-5 shadow-2xl shadow-blue-950/50 md:p-8" data-id="element-117" data-mp="alnyzyu4s7eq">
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-blue-600 px-3.5 py-1 text-[11px] font-bold uppercase tracking-widest text-white" data-id="element-118" data-mp="oknlazu68a7q">
        Meest gekozen
      </div>

      <p className="mt-2 text-center font-display text-xl font-black uppercase italic tracking-tight text-white md:text-2xl" data-id="element-119" data-mp="y9xeyk4ny0xy">
        Functionele Training <Accent data-id="element-120" data-mp="17bwvzcfoh50w">365™</Accent>
      </p>

      <ul className="mt-5 divide-y divide-white/10 border-y border-white/10" data-id="element-121" data-mp="17cykailpfo0k">
        {includes.map(({
        icon: Icon,
        label,
        detail,
        price
      }) => <li key={label} className="flex items-center justify-between gap-3 py-3" data-id="element-122" data-mp="1lyvmn56pnvek">
            <div className="flex min-w-0 items-start gap-3" data-id="element-123" data-mp="1qxp04xlzzmeq">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5" data-id="element-124" data-mp="1qx6wvh0x47xb">
                <Icon size={16} className="text-sky-400" aria-hidden="true" data-id="element-125" data-mp="1cxnznj9nagzs" />
              </span>
              <div className="min-w-0" data-id="element-126" data-mp="1gxpr6zlo1u1c">
                <p className="text-sm font-semibold leading-snug text-white md:text-base" data-id="element-127" data-mp="138e8q6qjf4d9">{label}</p>
                <p className="text-xs leading-snug text-slate-400 md:text-sm" data-id="element-128" data-mp="17ykuzzxkucsc">{detail}</p>
              </div>
            </div>
            <span className="shrink-0 text-sm text-slate-500 line-through" data-id="element-129" data-mp="tazrw60pcdkf">{price}</span>
          </li>)}
      </ul>

      <div className="mt-6 text-center" data-id="element-130" data-mp="y9t7c3zjuen8">
        <p className="text-sm text-slate-400" data-id="element-131" data-mp="y6oy2s788spp">
          Oorspronkelijke waarde <span className="text-slate-500 line-through" data-id="element-132" data-mp="ka1x8ec5liei">€119</span> · Volledige toegang vandaag voor
        </p>
        <p className="mt-3 flex items-baseline justify-center whitespace-nowrap font-display font-black italic leading-none text-white" aria-label="19 euro" data-id="element-133" data-mp="paftz08b15x8">
          <span className="mr-1.5 text-5xl text-sky-400 md:text-6xl" aria-hidden="true" data-id="element-134" data-mp="c9ltp1qzplbn">€</span>
          <span className="pr-[0.06em] text-[88px] tracking-tight md:text-[112px]" aria-hidden="true" data-id="element-135" data-mp="7gspcezfof03">19</span>
        </p>
        <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-bold uppercase tracking-wide text-emerald-400" data-id="element-136" data-mp="3fqzupvbjhbb">
          Je bespaart €100 · 84% korting
        </p>
        <p className="mt-2 text-xs text-slate-400 md:text-sm" data-id="element-137" data-mp="3pm5smaklm4ha">{footnote}</p>
      </div>

      <div className="mt-6" data-id="element-138" data-mp="3kow18pdnfiqt">
        <CTAButton href={PRODUCTS.ft365.checkoutUrl} fullWidth trackingProduct="ft365" onClick={markCheckoutStarted} data-id="element-139" data-mp="3kq750p73gqhx">
          Nu direct toegang
          <ArrowRightIcon size={18} className="transition-transform duration-200 group-hover/cta:translate-x-0.5" aria-hidden="true" data-id="element-140" data-mp="1ivzkclx25lys" />
        </CTAButton>
        <SecurePaymentNote className="mt-4" data-id="element-141" data-mp="2wt28h0wro20y" />
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-slate-500" data-id="element-142" data-mp="2wrzf8mbz1nhv">
          <span className="flex items-center gap-1" data-id="element-143" data-mp="31qkkx19zszdp"><Lock size={12} aria-hidden="true" data-id="element-144" data-mp="36iu2nu0yet4w" /> Veilig afrekenen</span>
          <span className="flex items-center gap-1" data-id="element-145" data-mp="36jncj3v7lyr9"><ZapIcon size={12} aria-hidden="true" data-id="element-146" data-mp="3akywasrejprj" /> Toegang binnen minuten</span>
          <span className="flex items-center gap-1" data-id="element-147" data-mp="2du6enqotlpnn"><ShieldCheck size={12} aria-hidden="true" data-id="element-148" data-mp="2dv83qz2o8cod" /> 7 dagen garantie</span>
        </div>
      </div>
    </div>;
};
export const Pricing = () => {
  return <Section className="bg-slate-900/40" id="pricing" data-id="element-149" data-mp="2iv466tvpzw1t">
      <div className="mx-auto max-w-lg" data-id="element-150" data-mp="2mu39ka1szkr5">
        <FadeIn className="mb-10 text-center" data-id="element-151" data-mp="2mx7itlu4l6oo">
          <p className="text-sm text-slate-400 md:text-base" data-id="element-152" data-mp="2rv0nubjozund">
            Je bent nog één stap verwijderd van een jaar vol kant-en-klare lessen…
          </p>
          <h2 className="mx-auto mt-3 font-display text-4xl font-black uppercase italic leading-[0.92] tracking-tight text-white md:text-6xl" data-id="element-153" data-mp="73qf8b4i0txv">
            Speciaal aanbod <Accent data-id="element-154" data-mp="235ada4h15xbm">bij lancering</Accent>
          </h2>
          <p className="mt-3 text-sm text-slate-400" data-id="element-155" data-mp="1z5t4p26b4k28">
            Delen, bonussen en 365 kant-en-klare sessies in één toegang.
          </p>
        </FadeIn>

        <FadeIn delay={0.05} data-id="element-156" data-mp="2d2yjpro00t9m">
          <OfferCard footnote="Eenmalige betaling · Geen abonnement · Direct toegang" data-id="element-157" data-mp="2867t4aekif9y" />
        </FadeIn>
      </div>
    </Section>;
};